import { lazy, Suspense, useEffect, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";

// Lazy load toasters - not needed for initial paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Eagerly load the landing page (home page)
import Index from "./pages/Index";
import { CookieConsent } from "./components/CookieConsent";

// Lazy load all other pages to reduce initial bundle size
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const Documents = lazy(() => import("./pages/Documents"));
const UploadDocument = lazy(() => import("./pages/UploadDocument"));
const DocumentView = lazy(() => import("./pages/DocumentView"));
const CreateSignatureRequest = lazy(() => import("./pages/CreateSignatureRequest"));
const SignatureRequestDetails = lazy(() => import("./pages/SignatureRequestDetails"));
const TemplateRequests = lazy(() => import("./pages/TemplateRequests"));
const PublicSign = lazy(() => import("./pages/PublicSign"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const SmsCheckout = lazy(() => import("./pages/SmsCheckout"));
const Contact = lazy(() => import("./pages/Contact"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminApiRequests = lazy(() => import("./pages/admin/AdminApiRequests"));
const AdminSmsPackages = lazy(() => import("./pages/admin/AdminSmsPackages"));
const AdminSmsPurchases = lazy(() => import("./pages/admin/AdminSmsPurchases"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">טוען...</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Suspense fallback={null}><Toaster /></Suspense>
        <Suspense fallback={null}><Sonner /></Suspense>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/documents/upload" element={<UploadDocument />} />
              <Route path="/documents/:id" element={<DocumentView />} />
              <Route path="/documents/:id/request" element={<CreateSignatureRequest />} />
              <Route path="/documents/:id/requests" element={<TemplateRequests />} />
              <Route path="/requests/:id" element={<SignatureRequestDetails />} />
              <Route path="/sign/:token" element={<PublicSign />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/sms-checkout" element={<SmsCheckout />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Admin routes */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
              <Route path="/admin/plans" element={<AdminPlans />} />
              <Route path="/admin/api-requests" element={<AdminApiRequests />} />
              <Route path="/admin/sms-packages" element={<AdminSmsPackages />} />
              <Route path="/admin/sms-purchases" element={<AdminSmsPurchases />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <CookieConsent />
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
